"use client";

import { useEffect, useRef, useState } from "react";

type Phase = "server" | "hidden" | "shown";

/**
 * Lifts its children into place the first time they scroll into view.
 *
 * The server renders them plainly visible, so nothing depends on JavaScript to
 * be readable, and anything already on screen at mount is left alone rather
 * than being hidden and re-shown.
 */
export function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  /** Milliseconds to stagger this item behind its neighbours. */
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>("server");

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPhase("shown");
      return;
    }
    if (node.getBoundingClientRect().top < window.innerHeight * 0.92) {
      setPhase("shown");
      return;
    }

    setPhase("hidden");
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setPhase("shown");
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -10% 0px" },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  const hidden = phase === "hidden";

  return (
    <div
      ref={ref}
      className={[
        "transition-[opacity,translate] duration-700 ease-out",
        className ?? "",
      ].join(" ")}
      style={{
        opacity: hidden ? 0 : 1,
        translate: hidden ? "0 20px" : "0 0",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}
